import { adminProjectsService } from '@/services/admin/admin-projects-service';
import { isClientError, models } from 'cesg-shared';
import { ProvisioningStatus } from 'cesg-shared/dist/models';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { isStaleInProgress } from './project-sharepoint-status';

/**
 * Bulk backfill of pre-existing projects (slice 3). A client-side loop over the *ensure* endpoint
 * `POST …/sharepoint/provision` — deliberately no server endpoint (D21) and no cron (D2).
 *
 * The loop never polls SharePoint itself: it POSTs, hands the returned status to `applyStatus`, and
 * then watches the reactive row. The host component's shared 5s poll already refreshes exactly the
 * rows that are provisioning, so waiting here is a local re-read, not a second network poller.
 */

/**
 * Every constant below is the slice-3 spec's *estimate*. Slice 1's manual verification checklist
 * was deferred to the user, so no real provisioning duration has ever been measured — recording
 * the real numbers is the slice-3 report's main job. Revisit all of these once it has.
 */

/** Spec's k = 2. D22 caps the server at 3, leaving one slot for a manual Provision click. */
const BULK_CONCURRENCY = 2;

/** Spec step 4. */
const THROTTLE_BACKOFF_MS = 15_000;

/** ≈2 minutes of backoff. Bounds a permanently-capped server rather than spinning forever. */
const MAX_THROTTLE_RETRIES = 8;

/** Spec: a project "must not be retried indefinitely" — so one automatic retry, then the operator. */
const MAX_PROVISION_ATTEMPTS = 2;

/**
 * Above the spec's "~20s–2 min" per project, and below `STALE_LEASE_WINDOW_MS` so a project that
 * times out here is still holding a lease the server considers fresh (which is what makes the
 * outcome `deferred` rather than `failed`).
 */
const PROJECT_TIMEOUT_MS = 5 * 60_000;

/** Local re-read of the reactive row. No network. */
const ROW_CHECK_INTERVAL_MS = 500;

export type BulkProvisioningPhase =
  | 'idle'
  | 'running'
  | 'waiting-for-stale'
  | 'done';

/** The three ways a project can leave the queue. `deferred` is D17's hand-back — see `runSweep`. */
type ProjectOutcome = 'ready' | 'failed' | 'deferred';

export interface BulkProvisioning {
  /** Work-list size, recomputed from the live project list — nothing is persisted (DoD 2). */
  pendingCount: ComputedRef<number>;
  isRunning: Ref<boolean>;
  isStopping: Ref<boolean>;
  phase: Ref<BulkProvisioningPhase>;
  total: Ref<number>;
  settled: ComputedRef<number>;
  readyIds: Ref<string[]>;
  failedIds: Ref<string[]>;
  deferredIds: Ref<string[]>;
  /** Counted for the report: how often D22's cap was actually hit. */
  throttleHits: Ref<number>;
  start: () => Promise<void>;
  stop: () => void;
}

/**
 * Every project the backfill still has work for: no ledger row at all, a failed run, or a run whose
 * lease has gone stale (D17 — nothing else is coming to move it along).
 *
 * `READY` is excluded because there is nothing to do, and *fresh* `IN_PROGRESS` because a live run
 * must not be double-started — re-POSTing one is a server-side no-op anyway (`claim()` returns
 * `null` and the controller answers `202` with the unchanged status).
 */
export function buildWorkList(
  projects: models.ListAdminProjectsResponse,
): string[] {
  return projects
    .filter(({ sharePoint }) => {
      if (!sharePoint) return true;
      if (sharePoint.status === ProvisioningStatus.FAILED) return true;
      return (
        sharePoint.status === ProvisioningStatus.IN_PROGRESS &&
        isStaleInProgress(sharePoint)
      );
    })
    .map(({ id }) => id);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useBulkProvisioning(options: {
  projects: Ref<models.ListAdminProjectsResponse>;
  applyStatus: (
    projectId: string,
    sharePoint: models.ProjectWorkspaceStatus | null,
  ) => void;
}): BulkProvisioning {
  const { projects, applyStatus } = options;

  const isRunning = ref(false);
  const isStopping = ref(false);
  const phase = ref<BulkProvisioningPhase>('idle');
  const total = ref(0);
  const readyIds = ref<string[]>([]);
  const failedIds = ref<string[]>([]);
  const deferredIds = ref<string[]>([]);
  const throttleHits = ref(0);

  const pendingCount = computed(() => buildWorkList(projects.value).length);
  const settled = computed(
    () =>
      readyIds.value.length + failedIds.value.length + deferredIds.value.length,
  );

  /** Mutable, not reactive: the workers shift from it and requeue onto it. */
  let queue: string[] = [];
  /** POSTs that ended in a *failure*, per project. 429s have their own budget. */
  let failures = new Map<string, number>();
  let throttleRetries = new Map<string, number>();
  let stopRequested = false;
  /** The single D17 second pass. It grants one extra attempt and no more. */
  let isSweepPass = false;

  function findProject(
    projectId: string,
  ): models.ListAdminProjectResponse | undefined {
    return projects.value.find((project) => project.id === projectId);
  }

  /**
   * Reads the outcome off the reactive row, or `null` while it is still moving. `sharePoint` being
   * `null` here means the status poll got a `404`: the ledger row is genuinely gone, so there is
   * nothing left to wait for.
   */
  function readOutcome(projectId: string): ProjectOutcome | null {
    const project = findProject(projectId);
    if (!project) return 'failed';
    const { sharePoint } = project;
    if (!sharePoint) return 'failed';
    if (sharePoint.status === ProvisioningStatus.READY) return 'ready';
    if (sharePoint.status === ProvisioningStatus.FAILED) return 'failed';
    if (isStaleInProgress(sharePoint)) return 'deferred';
    return null;
  }

  /**
   * Waits for the shared poll to write a terminal status onto the row. Deliberately keeps waiting
   * after `stop()`: stopping means "start nothing new", and an in-flight project must still be
   * accounted for.
   */
  async function awaitRow(projectId: string): Promise<ProjectOutcome> {
    const deadline = Date.now() + PROJECT_TIMEOUT_MS;
    for (;;) {
      const outcome = readOutcome(projectId);
      if (outcome) return outcome;
      // D17: the server left the row IN_PROGRESS and returned. Release the slot now; the sweep
      // picks it up once the lease goes stale and the server will accept a re-POST.
      if (Date.now() >= deadline) return 'deferred';
      await wait(ROW_CHECK_INTERVAL_MS);
    }
  }

  function record(target: Ref<string[]>, projectId: string) {
    if (!target.value.includes(projectId)) target.value.push(projectId);
  }

  function recordFailure(projectId: string) {
    const count = (failures.get(projectId) ?? 0) + 1;
    failures.set(projectId, count);

    // Requeued at the *back*, so a transient cause gets time and the sweep is not stalled behind
    // one project. The sweep pass never requeues — it is already the extra attempt.
    if (!isSweepPass && count < MAX_PROVISION_ATTEMPTS) {
      queue.push(projectId);
      return;
    }
    record(failedIds, projectId);
  }

  async function provisionOne(projectId: string): Promise<void> {
    for (;;) {
      let status: models.ProjectWorkspaceStatus;
      try {
        status = await adminProjectsService.provisionSharePointSite(projectId);
      } catch (error) {
        if (!isClientError(error, 'SHAREPOINT_PROVISIONING_BUSY')) {
          recordFailure(projectId);
          return;
        }

        // D22 backpressure. Never a failure, and on its own budget so a permanently-capped server
        // cannot spin here forever.
        throttleHits.value += 1;
        const hits = (throttleRetries.get(projectId) ?? 0) + 1;
        throttleRetries.set(projectId, hits);
        if (hits >= MAX_THROTTLE_RETRIES) {
          record(deferredIds, projectId);
          return;
        }

        await wait(THROTTLE_BACKOFF_MS);
        if (stopRequested) {
          record(deferredIds, projectId);
          return;
        }
        continue;
      }

      applyStatus(projectId, status);

      const outcome = await awaitRow(projectId);
      if (outcome === 'ready') record(readyIds, projectId);
      else if (outcome === 'deferred') record(deferredIds, projectId);
      else recordFailure(projectId);
      return;
    }
  }

  async function runWorkers(): Promise<void> {
    const worker = async () => {
      while (!stopRequested) {
        const projectId = queue.shift();
        if (projectId === undefined) return;
        await provisionOne(projectId);
      }
    };
    await Promise.all(Array.from({ length: BULK_CONCURRENCY }, () => worker()));
  }

  /** A D17 hand-back: the server left the row `IN_PROGRESS` and returned. */
  function isHandedBack(projectId: string): boolean {
    return (
      findProject(projectId)?.sharePoint?.status ===
      ProvisioningStatus.IN_PROGRESS
    );
  }

  /** True once the server would accept a re-POST for this row (D17's stale window). */
  function isReclaimable(projectId: string): boolean {
    const project = findProject(projectId);
    if (!project?.sharePoint) return true;
    if (project.sharePoint.status !== ProvisioningStatus.IN_PROGRESS)
      return true;
    return isStaleInProgress(project.sharePoint);
  }

  /**
   * Accounts any deferred row that has since reached a terminal ledger status. Most of them have,
   * by the time the sweep runs: the shared 5s poll keeps refreshing them, since a deferred row
   * stays `isProvisioningActive` for the full window.
   *
   * A `null` row is deliberately *not* treated as failed here — unlike in `readOutcome`, where the
   * loop had already written a status, `null` here just means the project was never provisioned
   * (its throttle budget ran out before a single POST got through).
   */
  function settleFinishedDeferrals() {
    for (const projectId of [...deferredIds.value]) {
      const sharePoint = findProject(projectId)?.sharePoint;
      if (!sharePoint) continue;
      if (
        sharePoint.status !== ProvisioningStatus.READY &&
        sharePoint.status !== ProvisioningStatus.FAILED
      ) {
        continue;
      }
      deferredIds.value = deferredIds.value.filter((id) => id !== projectId);
      record(
        sharePoint.status === ProvisioningStatus.READY ? readyIds : failedIds,
        projectId,
      );
    }
  }

  /**
   * D17's single second pass. A handed-back row is neither `READY` nor `FAILED` and nothing else
   * will move it (D2), so once the main queue drains the loop waits for those rows to cross the
   * stale window and re-POSTs each one exactly once. Worst case this adds a ~10-minute idle tail;
   * in exchange "every project reaches READY or FAILED unattended" holds literally.
   *
   * Only hand-backs are swept. A project deferred because its throttle budget ran out holds no
   * lease, so there is nothing to wait for and nothing new to try — the server was capped for two
   * solid minutes. It stays in the summary with its own Retry button.
   */
  async function runSweep(): Promise<void> {
    if (stopRequested || deferredIds.value.length === 0) return;

    settleFinishedDeferrals();

    let candidates = deferredIds.value.filter(isHandedBack);
    if (stopRequested || candidates.length === 0) return;

    phase.value = 'waiting-for-stale';
    while (!stopRequested && !candidates.every(isReclaimable)) {
      await wait(ROW_CHECK_INTERVAL_MS);
      settleFinishedDeferrals();
      candidates = candidates.filter((id) => deferredIds.value.includes(id));
      if (candidates.length === 0) break;
    }
    if (stopRequested || candidates.length === 0) return;

    isSweepPass = true;
    queue = candidates;
    deferredIds.value = deferredIds.value.filter(
      (id) => !candidates.includes(id),
    );
    // The sweep is the extra attempt: it does not consume the failure budget, and a row that also
    // hit 429s on the way here gets a fresh throttle budget for it.
    for (const projectId of queue) throttleRetries.delete(projectId);

    phase.value = 'running';
    await runWorkers();
  }

  async function start(): Promise<void> {
    if (isRunning.value) return;

    queue = buildWorkList(projects.value);
    failures = new Map();
    throttleRetries = new Map();
    stopRequested = false;
    isSweepPass = false;
    readyIds.value = [];
    failedIds.value = [];
    deferredIds.value = [];
    throttleHits.value = 0;
    total.value = queue.length;
    isStopping.value = false;
    isRunning.value = true;
    phase.value = 'running';

    try {
      await runWorkers();
      await runSweep();
    } finally {
      isRunning.value = false;
      isStopping.value = false;
      phase.value = 'done';
    }
  }

  function stop(): void {
    if (!isRunning.value) return;
    stopRequested = true;
    isStopping.value = true;
  }

  return {
    pendingCount,
    isRunning,
    isStopping,
    phase,
    total,
    settled,
    readyIds,
    failedIds,
    deferredIds,
    throttleHits,
    start,
    stop,
  };
}
